// Project Penyedia (/project-penyedia; Simulasi Pluto v4 projects() for a
// Penyedia): where a Penyedia's work starts.
//   header         plain spacing   h1, one sentence, Buat Project
//   alur           section         the four stages in one line, and why the
//                                  government stages are left out
//   temukan        section         Temukan Paket (penyedia/Opportunities.tsx)
//   projects       cards           one card per paket project: its stage, the
//                                  instansi and jenis, metode, progress % with
//                                  done/total activities, Kelola; then a
//                                  dashed Buat project baru card
// Progress needs each project's checklist; it is read for at most
// PROGRESS_SAMPLE projects (the rest show their stage only).
import * as API from "../api";
import type { Project } from "../api";
import { JENIS_LABEL, METODE_LABEL, paketTitle } from "../project/stages";
import { projectPath, useProjects } from "../shell/Projects";
import { EmptyState, Notice, PageHeader, SkeletonRows, useAsync, useTitle } from "../ui/components";
import { Glyph } from "../ui/icons";
import { Opportunities } from "./Opportunities";
import { stageLabel } from "./shared";

export const PROGRESS_SAMPLE = 24;

type Progress = Record<string, { done: number; total: number }>;

async function loadProgress(projects: Project[]): Promise<Progress> {
  const details = await Promise.all(projects.slice(0, PROGRESS_SAMPLE).map((p) => API.getProject(p.id).catch(() => null)));
  const out: Progress = {};
  for (const d of details) {
    if (!d) continue;
    const items = d.checklist.flatMap((g) => g.items);
    out[d.project.id] = { done: items.filter((i) => i.done).length, total: items.length };
  }
  return out;
}

export function ProjectsPenyediaScreen() {
  useTitle("Project Penyedia");
  const projects = useProjects();
  const paket = (projects?.list.data ?? []).filter((p) => p.kind === "paket");
  const key = paket.map((p) => `${p.id}:${p.stage}`).join(",");
  const progress = useAsync(() => loadProgress(paket), [key]);
  const create = projects ? (
    <button type="button" className="btn btn-primary btn-sm" aria-haspopup="dialog" onClick={projects.openCreate}>
      <Glyph name="add" size={16} />
      <span>Project Baru</span>
    </button>
  ) : undefined;

  return (
    <>
      <PageHeader
        divided
        title="Project Penyedia"
        lead="Seluruh pekerjaan dimulai dari pencarian paket dan analisis persyaratan hingga penawaran, kontrak, serta serah terima."
        actions={create}
      />
      <section className="pks" aria-labelledby="pp-alur-title">
        <div className="pks-head-text">
          <h2 id="pp-alur-title" className="pks-title">
            Persiapan Dokumen Penawaran, Pelaksanaan Pemilihan, Kontrak, Serah Terima
          </h2>
          <p className="pks-lead">
            Tahap Perencanaan Pengadaan dan Persiapan Pengadaan adalah pekerjaan pemerintah, jadi tidak ditampilkan. Gunakan project untuk mencari peluang, membaca persyaratan, dan menyiapkan dokumen penawaran.
          </p>
        </div>
      </section>
      <Opportunities />
      {projects?.list.loading && !projects.list.data ? (
        <SkeletonRows count={3} label="Memuat project" />
      ) : projects?.list.error ? (
        <Notice tone="danger" title="Project belum termuat" action={<button type="button" className="btn btn-secondary" onClick={projects.list.reload}>Muat ulang</button>}>
          <p>{API.errorMessage(projects.list.error)}</p>
        </Notice>
      ) : paket.length === 0 ? (
        <EmptyState icon="box" title="Belum ada project paket" body="Buat project untuk paket yang ingin diikuti, atau jadikan project dari paket yang Anda catat." action={create} />
      ) : (
        <section className="region" aria-labelledby="pp-list-title">
          <h2 id="pp-list-title" className="pks-title">
            Project perusahaan
          </h2>
          <ul className="pbj-cards" aria-label="Project paket">
            {paket.map((p) => {
              const pr = progress.data?.[p.id];
              const pct = pr && pr.total > 0 ? Math.round((pr.done / pr.total) * 100) : 0;
              const meta = [p.instansi || "Instansi belum diisi", p.jenisPengadaan ? JENIS_LABEL[p.jenisPengadaan] : null].filter(Boolean).join(", ");
              return (
                <li key={p.id}>
                  <a className="pbj-card" href={projectPath(p.id)}>
                    <span className="pbj-card-top">
                      <Glyph name="box" size={18} />
                      <span className="pbj-card-stage">{stageLabel(p.stage ?? "persiapan_pemilihan")}</span>
                    </span>
                    <span className="pbj-card-title">{paketTitle(p)}</span>
                    <span className="pbj-card-meta">{meta}</span>
                    <span className="pbj-card-meta">Penyedia, {p.metode ? METODE_LABEL[p.metode] : "metode belum diisi"}</span>
                    <span className="pbj-card-progress">
                      <span className="pbj-card-row">
                        <span>Progres paket</span>
                        <strong className="num">{pr ? `${pct}%` : "-"}</strong>
                      </span>
                      <span className="pbj-bar" aria-hidden="true">
                        <span className="pbj-bar-fill" style={{ width: `${pct}%` }} />
                      </span>
                      <span className="pbj-card-row">
                        <span className="num">{pr ? `${pr.done}/${pr.total} aktivitas` : "Aktivitas belum terbaca"}</span>
                        <span className="pbj-card-open">
                          Kelola <Glyph name="chevronRight" size={16} />
                        </span>
                      </span>
                    </span>
                  </a>
                </li>
              );
            })}
            {projects ? (
              <li>
                <button type="button" className="pbj-card pbj-card-new" aria-haspopup="dialog" onClick={projects.openCreate}>
                  <Glyph name="add" size={24} />
                  <span className="pbj-card-title">Buat project baru</span>
                  <span className="pbj-card-meta">Mulai dari Persiapan Dokumen Penawaran</span>
                </button>
              </li>
            ) : null}
          </ul>
        </section>
      )}
    </>
  );
}
